/**
 * validateLedgerDefinition
 *
 * The whole-definition validator: reads an untyped document, rebuilds a typed `LedgerDefinition`
 * from the checked parts (no cast), and returns either that definition or every problem found, each
 * at a dotted path such as `accrualRules[1].bind.claimAmount.typeId`. A definition is valid as a
 * whole or not at all: unlike `createFormDeriveEngineConfig`, nothing is dropped, because a dropped
 * accrual rule means claims silently go missing. Extra keys are stripped. Column existence is not
 * checked here, since it needs I/O (the settings page does it). A `firstBound` never nests, and a
 * table lookup never reads the rule's own source table. An absent `adjustmentReasons` reads as `[]`,
 * so a definition stored before reasons existed stays valid. A terms binding without a
 * `counterpartyReferenceKey` reads the counterparty's own record, so its entity must be the
 * counterparty entity; without a `shapeField` it needs a fallback and takes no params or priority. A `file`
 * channel holds at least one statement mapping, unique by `mappingId`, whose dimensions are declared and whose
 * `reference` and `receivedAt` never read `sumOfLines`, and an `originalTotal`, when present, is a `column`; its
 * `templateHeadings`, when present, are strings, unique except that a blank heading, a column with no heading, may repeat, and include every header the mapping reads (`ledgerStatementMappingHeaders`), `originalTotal`'s included, and `totalMustMatch` and `requireOriginal` are booleans. An absent
 * `lifecycle` stays absent; each of its durations holds whole non-negative years, months, weeks or days with at
 * least one above zero, and its cron must parse through `parseLedgerCron`. An absent `aging.unappliedAfterDays`
 * stays absent; present, it is an integer ≥ 1. An absent `creditLineReasonId` stays absent; present, it names a declared
 * adjustment reason that targets a claim. A `matchGroupLabel` is stored trimmed, and a blank one is dropped. No `access` right may be `ledgerAdmin` or `orgsAdmin`, since
 * those grant no ledger data access. Consumers: the ledger definition reader and settings
 * page (SYD-2707, SYD-2710).
 */

import { USER_RIGHTS_METADATA } from '../AccessControl.types';
import { LEDGER_DURATION_UNITS, LEDGER_TERM_PARAMS } from '../System.Ledger.Definition.types';
import { LEDGER_INSTRUMENTS } from '../System.Ledger.types';
import { ledgerMomentOfValue } from './ledgerMomentOfValue';
import { ledgerStatementMappingHeaders } from './ledgerStatementMappingHeaders';
import { parseLedgerCron } from './parseLedgerCron';
import type { UserRightId } from '../AccessControl.types';
import type { DateLocale, NumberLocale } from '../System.Agent.ExtractionCoercion.types';
import type {
    LedgerAccess,
    LedgerAccrualBind,
    LedgerAdjustmentReason,
    LedgerAdjustmentTarget,
    LedgerAccrualRule,
    LedgerAgingBuckets,
    LedgerBindSource,
    LedgerCalendarSettings,
    LedgerChannel,
    LedgerClaimKind,
    LedgerDefinitionCheck,
    LedgerDefinitionProblem,
    LedgerDimension,
    LedgerDuration,
    LedgerLifecyclePolicy,
    LedgerMatchKey,
    LedgerMatchRule,
    LedgerMatchTolerance,
    LedgerRowValueSource,
    LedgerStatementMapping,
    LedgerStatementRowTest,
    LedgerStatementValueSource,
    LedgerTableLookupKey,
    LedgerTableLookupSource,
    LedgerTermParam,
    LedgerTermShape,
    LedgerTermTrigger,
    LedgerTermsBinding,
} from '../System.Ledger.Definition.types';
import type { FormDeriveReferenceProp } from '../System.FormDerive.types';
import type { WorkflowRowFilter, WorkflowRowFilterComparable } from '../System.Workflow.types';
import { isColumnPathTypeId } from '../formDeriveEngine/isColumnPathTypeId';
import { isColumnTypeId } from '../formDeriveEngine/isColumnTypeId';
import { isRecordValue } from '../formDeriveEngine/isRecordValue';

const ID_PATTERN = /^[A-Za-z0-9_-]+$/;
const CURRENCY_PATTERN = /^[A-Z]{3}$/;
const CELL_ADDRESS_PATTERN = /^[A-Z]{1,3}[1-9][0-9]*$/;
const VALUE_SOURCE_KINDS = '"cell", "besideText", "column" or "row"';

type Declared = { claimKindIds: ReadonlySet<string>; dimensionIds: ReadonlySet<string>; counterpartyEntityId: string };

type Reader = { report: (path: string, message: string) => void };

function child(path: string, key: string): string {
    return path === '' ? key : `${path}.${key}`;
}

function item(path: string, index: number): string {
    return `${path}[${index}]`;
}

function objectAt(r: Reader, value: unknown, path: string): Record<string, unknown> {
    if (isRecordValue(value)) return value;
    r.report(path, 'must be an object');
    return {};
}

function arrayAt(r: Reader, value: unknown, path: string): unknown[] {
    if (Array.isArray(value)) return value;
    r.report(path, 'must be a list');
    return [];
}

function textAt(r: Reader, value: unknown, path: string): string {
    if (typeof value === 'string' && value.trim() !== '') return value;
    r.report(path, 'must be a non-empty string');
    return '';
}

function headingAt(r: Reader, value: unknown, path: string): string {
    if (typeof value === 'string') return value;
    r.report(path, 'must be a string');
    return '';
}

function booleanAt(r: Reader, value: unknown, path: string): boolean {
    if (typeof value === 'boolean') return value;
    r.report(path, 'must be true or false');
    return false;
}

function idAt(r: Reader, value: unknown, path: string): string {
    if (typeof value === 'string' && ID_PATTERN.test(value)) return value;
    r.report(path, 'must be an id of letters, digits, "_" or "-"');
    return '';
}

function integerAt(r: Reader, value: unknown, path: string, min: number, max = Number.MAX_SAFE_INTEGER): number {
    if (Number.isSafeInteger(value) && typeof value === 'number' && value >= min && value <= max) return value;
    r.report(path, max === Number.MAX_SAFE_INTEGER ? `must be an integer ≥ ${min}` : `must be an integer from ${min} to ${max}`);
    return min;
}

function numberAt(r: Reader, value: unknown, path: string, min: number, max: number): number {
    if (typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max) return value;
    r.report(path, `must be a number from ${min} to ${max}`);
    return min;
}

function oneOfAt<T extends string>(r: Reader, value: unknown, path: string, allowed: readonly [T, ...T[]]): T {
    const found = allowed.find((option) => option === value);
    if (found !== undefined) return found;
    r.report(path, `must be one of ${allowed.map((option) => `"${option}"`).join(', ')}`);
    return allowed[0];
}

function momentAt(r: Reader, value: unknown, path: string): string {
    const east = ledgerMomentOfValue(value, 'UTC+14');
    const west = ledgerMomentOfValue(value, 'UTC-12');
    if (typeof value === 'string' && east !== null && west !== null && east.toMillis() === west.toMillis()) return value;
    r.report(path, 'must be an ISO-8601 moment with a date and a Z or explicit offset');
    return '';
}

function isUserRightId(value: unknown): value is UserRightId {
    return typeof value === 'string' && Object.prototype.hasOwnProperty.call(USER_RIGHTS_METADATA, value);
}

function rightAt(r: Reader, value: unknown, path: string): UserRightId {
    if (isUserRightId(value)) return value;
    r.report(path, 'must be a known user right');
    return 'ledgerRead';
}

function declaredAt(r: Reader, value: unknown, path: string, declared: ReadonlySet<string>, noun: string): string {
    const id = idAt(r, value, path);
    if (id !== '' && !declared.has(id)) r.report(path, `must name a declared ${noun}`);
    return id;
}

function checkUnique(r: Reader, ids: readonly string[], listPath: string, key: string): void {
    ids.forEach((id, index) => {
        if (id !== '' && ids.indexOf(id) !== index) r.report(child(item(listPath, index), key), `duplicates "${id}"`);
    });
}

function readDimension(r: Reader, value: unknown, path: string): LedgerDimension {
    const raw = objectAt(r, value, path);
    return {
        dimensionId: idAt(r, raw.dimensionId, child(path, 'dimensionId')),
        label: textAt(r, raw.label, child(path, 'label')),
        valueType: oneOfAt(r, raw.valueType, child(path, 'valueType'), ['text', 'number']),
    };
}

function readClaimKind(r: Reader, value: unknown, path: string): LedgerClaimKind {
    const raw = objectAt(r, value, path);
    return {
        claimKindId: idAt(r, raw.claimKindId, child(path, 'claimKindId')),
        label: textAt(r, raw.label, child(path, 'label')),
    };
}

function readComparable(r: Reader, value: unknown, path: string): WorkflowRowFilterComparable {
    if (value === null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
    r.report(path, isRecordValue(value) && 'sourceType' in value
        ? 'must be a literal; there is no workflow context to resolve a value source against'
        : 'must be a string, number, boolean or null');
    return null;
}

function readRowFilter(r: Reader, value: unknown, path: string): WorkflowRowFilter {
    const raw = objectAt(r, value, path);
    const columnId = textAt(r, raw.columnId, child(path, 'columnId'));
    if (raw.operator === 'in') {
        return {
            columnId,
            operator: 'in',
            values: arrayAt(r, raw.values, child(path, 'values'))
                .map((operand, index) => readComparable(r, operand, item(child(path, 'values'), index))),
        };
    }
    if (raw.operator !== '==') r.report(child(path, 'operator'), 'must be "==" or "in"');
    return { columnId, operator: '==', value: readComparable(r, raw.value, child(path, 'value')) };
}

function readReferenceProp(r: Reader, value: unknown, path: string): FormDeriveReferenceProp {
    const raw = objectAt(r, value, path);
    if (raw.kind === 'field') return { kind: 'field', fieldId: textAt(r, raw.fieldId, child(path, 'fieldId')) };
    if (raw.kind === 'reference') {
        return { kind: 'reference', referenceKey: textAt(r, raw.referenceKey, child(path, 'referenceKey')) };
    }
    if (raw.kind !== 'name') r.report(child(path, 'kind'), 'must be "name", "field" or "reference"');
    return { kind: 'name' };
}

const COLUMN_PATH_MESSAGE = 'must be a column typeId, or parent.sub for a composite sub-field';

function columnPathAt(r: Reader, value: unknown, path: string): string {
    if (isColumnPathTypeId(value)) return value;
    r.report(path, COLUMN_PATH_MESSAGE);
    return '';
}

function readTableLookupKey(r: Reader, value: unknown, path: string): LedgerTableLookupKey {
    const raw = objectAt(r, value, path);
    return {
        lookupTypeId: columnPathAt(r, raw.lookupTypeId, child(path, 'lookupTypeId')),
        lineTypeId: columnPathAt(r, raw.lineTypeId, child(path, 'lineTypeId')),
    };
}

function readTableLookup(r: Reader, raw: Record<string, unknown>, path: string, sourceTableId: string): LedgerTableLookupSource {
    const tableIdPath = child(path, 'tableId');
    const tableId = textAt(r, raw.tableId, tableIdPath);
    if (tableId !== '' && tableId === sourceTableId) r.report(tableIdPath, "must name a table other than the rule's source table");
    const keysPath = child(path, 'keys');
    const keys = listAt(r, raw.keys, keysPath, (entry, entryPath) => readTableLookupKey(r, entry, entryPath));
    if (Array.isArray(raw.keys) && keys.length === 0) r.report(keysPath, 'must hold at least one key');
    return { kind: 'tableLookup', tableId, keys, valueTypeId: columnPathAt(r, raw.valueTypeId, child(path, 'valueTypeId')) };
}

function readRowValueSource(r: Reader, value: unknown, path: string, sourceTableId: string): LedgerRowValueSource {
    const raw = objectAt(r, value, path);
    if (raw.kind === 'tableLookup') return readTableLookup(r, raw, path, sourceTableId);
    if (raw.kind === 'rowReferenceProp') {
        const referenceTypeId = isColumnTypeId(raw.referenceTypeId) ? raw.referenceTypeId : '';
        if (referenceTypeId === '') r.report(child(path, 'referenceTypeId'), 'must be an undotted column typeId');
        return { kind: 'rowReferenceProp', referenceTypeId, prop: readReferenceProp(r, raw.prop, child(path, 'prop')) };
    }
    if (raw.kind === 'firstBound') {
        r.report(path, 'must not nest firstBound');
        return { kind: 'sourceCell', typeId: '' };
    }
    if (raw.kind !== 'sourceCell') {
        r.report(child(path, 'kind'), 'must be "sourceCell", "rowReferenceProp", "tableLookup" or "firstBound"');
        return { kind: 'sourceCell', typeId: '' };
    }
    return { kind: 'sourceCell', typeId: columnPathAt(r, raw.typeId, child(path, 'typeId')) };
}

function readBindSource(r: Reader, value: unknown, path: string, sourceTableId: string): LedgerBindSource {
    if (!isRecordValue(value) || value.kind !== 'firstBound') return readRowValueSource(r, value, path, sourceTableId);
    const sourcesPath = child(path, 'sources');
    const sources = listAt(r, value.sources, sourcesPath, (entry, entryPath) => readRowValueSource(r, entry, entryPath, sourceTableId));
    if (Array.isArray(value.sources) && sources.length === 0) r.report(sourcesPath, 'must hold at least one source');
    return { kind: 'firstBound', sources };
}

function readBind(r: Reader, value: unknown, path: string, declared: Declared, sourceTableId: string): LedgerAccrualBind {
    const raw = objectAt(r, value, path);
    const dimensionsPath = child(path, 'dimensions');
    const dimensions = raw.dimensions === undefined
        ? undefined
        : Object.fromEntries(Object.entries(objectAt(r, raw.dimensions, dimensionsPath)).map(([dimensionId, source]) => {
            const sourcePath = child(dimensionsPath, dimensionId);
            if (!declared.dimensionIds.has(dimensionId)) r.report(sourcePath, 'must name a declared dimension');
            return [dimensionId, readBindSource(r, source, sourcePath, sourceTableId)];
        }));
    const member = (key: string): LedgerBindSource => readBindSource(r, raw[key], child(path, key), sourceTableId);
    return {
        claimAmount: member('claimAmount'),
        ...(raw.claimQuantity === undefined ? {} : { claimQuantity: member('claimQuantity') }),
        triggerDate: member('triggerDate'),
        matchGroup: member('matchGroup'),
        counterparty: member('counterparty'),
        ...(raw.grainRef === undefined ? {} : { grainRef: member('grainRef') }),
        ...(dimensions === undefined ? {} : { dimensions }),
    };
}

function readAccrualRule(r: Reader, value: unknown, path: string, declared: Declared): LedgerAccrualRule {
    const raw = objectAt(r, value, path);
    const sourcePath = child(path, 'source');
    const source = objectAt(r, raw.source, sourcePath);
    const formTypeIdsPath = child(sourcePath, 'formTypeIds');
    const formTypeIds = arrayAt(r, source.formTypeIds, formTypeIdsPath)
        .map((formTypeId, index) => textAt(r, formTypeId, item(formTypeIdsPath, index)));
    if (Array.isArray(source.formTypeIds) && formTypeIds.length === 0) r.report(formTypeIdsPath, 'must name at least one form type');
    const whenPath = child(path, 'when');
    const tableId = textAt(r, source.tableId, child(sourcePath, 'tableId'));
    return {
        ruleId: idAt(r, raw.ruleId, child(path, 'ruleId')),
        claimKindId: declaredAt(r, raw.claimKindId, child(path, 'claimKindId'), declared.claimKindIds, 'claim kind'),
        source: { formTypeIds, tableId },
        when: arrayAt(r, raw.when, whenPath).map((filter, index) => readRowFilter(r, filter, item(whenPath, index))),
        bind: readBind(r, raw.bind, child(path, 'bind'), declared, tableId),
    };
}

function checkAccrualOverlap(r: Reader, rules: readonly LedgerAccrualRule[]): void {
    const owners = new Map<string, number>();
    rules.forEach((rule, ruleIndex) => {
        rule.source.formTypeIds.forEach((formTypeId, index) => {
            const key = JSON.stringify([rule.claimKindId, formTypeId, rule.source.tableId]);
            const owner = owners.get(key);
            if (owner === undefined) {
                owners.set(key, ruleIndex);
            } else if (owner !== ruleIndex) {
                r.report(
                    item(`accrualRules[${ruleIndex}].source.formTypeIds`, index),
                    `accrues claim kind "${rule.claimKindId}" on this form type and table, as accrualRules[${owner}] already does`,
                );
            }
        });
    });
}

function readRowTest(r: Reader, value: unknown, path: string): LedgerStatementRowTest {
    const raw = objectAt(r, value, path);
    const header = textAt(r, raw.header, child(path, 'header'));
    if (raw.is !== undefined) return { header, is: oneOfAt(r, raw.is, child(path, 'is'), ['blank']) };
    const equalsPath = child(path, 'equals');
    const equals = listAt(r, raw.equals, equalsPath, (entry, entryPath) => textAt(r, entry, entryPath));
    if (Array.isArray(raw.equals) && equals.length === 0) r.report(equalsPath, 'must hold at least one text');
    return { header, equals };
}

function readValueSource(r: Reader, raw: Record<string, unknown>, path: string, kinds: string): LedgerStatementValueSource {
    if (raw.kind === 'cell') {
        const addressPath = child(path, 'address');
        if (typeof raw.address === 'string' && CELL_ADDRESS_PATTERN.test(raw.address)) return { kind: 'cell', address: raw.address };
        r.report(addressPath, 'must be an A1 cell address such as "B3"');
        return { kind: 'cell', address: '' };
    }
    if (raw.kind === 'besideText') return { kind: 'besideText', text: textAt(r, raw.text, child(path, 'text')) };
    if (raw.kind === 'row') {
        return {
            kind: 'row',
            where: readRowTest(r, raw.where, child(path, 'where')),
            header: textAt(r, raw.header, child(path, 'header')),
        };
    }
    if (raw.kind !== 'column') {
        r.report(child(path, 'kind'), `must be ${kinds}`);
        return { kind: 'column', header: '' };
    }
    return { kind: 'column', header: textAt(r, raw.header, child(path, 'header')) };
}

function readStatementLines(r: Reader, value: unknown, path: string, declared: Declared): LedgerStatementMapping['lines'] {
    const raw = objectAt(r, value, path);
    const dimensionsPath = child(path, 'dimensions');
    const dimensions = raw.dimensions === undefined
        ? undefined
        : Object.fromEntries(Object.entries(objectAt(r, raw.dimensions, dimensionsPath)).map(([dimensionId, header]) => {
            const headerPath = child(dimensionsPath, dimensionId);
            if (!declared.dimensionIds.has(dimensionId)) r.report(headerPath, 'must name a declared dimension');
            return [dimensionId, textAt(r, header, headerPath)];
        }));
    return {
        amount: textAt(r, raw.amount, child(path, 'amount')),
        ...(raw.matchGroup === undefined ? {} : { matchGroup: textAt(r, raw.matchGroup, child(path, 'matchGroup')) }),
        ...(dimensions === undefined ? {} : { dimensions }),
    };
}

function readOriginalTotal(r: Reader, value: unknown, path: string): { kind: 'column'; header: string } {
    const raw = objectAt(r, value, path);
    if (raw.kind !== 'column') r.report(child(path, 'kind'), 'must be "column"');
    return { kind: 'column', header: textAt(r, raw.header, child(path, 'header')) };
}

function readTemplateHeadings(r: Reader, value: unknown, path: string, mapping: LedgerStatementMapping): string[] {
    const headings = listAt(r, value, path, (entry, entryPath) => headingAt(r, entry, entryPath));
    if (headings.length === 0) {
        if (Array.isArray(value)) r.report(path, 'must hold at least one heading');
        return headings;
    }
    const trimmed = headings.map((heading) => heading.trim());
    trimmed.forEach((heading, index) => {
        if (heading !== '' && trimmed.indexOf(heading) !== index) r.report(item(path, index), `duplicates "${heading}"`);
    });
    const keys = new Set(headings.map((heading) => heading.trim().toLowerCase()));
    ledgerStatementMappingHeaders(mapping)
        .filter((header) => header !== '' && !keys.has(header.trim().toLowerCase()))
        .forEach((header) => r.report(path, `must include "${header}", which this mapping reads`));
    return headings;
}

function readStatementMapping(r: Reader, value: unknown, path: string, declared: Declared): LedgerStatementMapping {
    const raw = objectAt(r, value, path);
    const totalPath = child(path, 'total');
    const total = objectAt(r, raw.total, totalPath);
    const numberLocalePath = child(path, 'numberLocale');
    const dateLocalePath = child(path, 'dateLocale');
    const mapping: LedgerStatementMapping = {
        mappingId: idAt(r, raw.mappingId, child(path, 'mappingId')),
        label: textAt(r, raw.label, child(path, 'label')),
        counterpartyId: textAt(r, raw.counterpartyId, child(path, 'counterpartyId')),
        instrument: oneOfAt(r, raw.instrument, child(path, 'instrument'), LEDGER_INSTRUMENTS),
        ...(raw.sheet === undefined ? {} : { sheet: textAt(r, raw.sheet, child(path, 'sheet')) }),
        headerRow: integerAt(r, raw.headerRow, child(path, 'headerRow'), 1),
        lines: readStatementLines(r, raw.lines, child(path, 'lines'), declared),
        skipRows: listAt(r, raw.skipRows, child(path, 'skipRows'), (entry, entryPath) => readRowTest(r, entry, entryPath)),
        total: total.kind === 'sumOfLines'
            ? { kind: 'sumOfLines' }
            : readValueSource(r, total, totalPath, '"cell", "besideText", "column", "row" or "sumOfLines"'),
        ...(raw.reference === undefined
            ? {}
            : { reference: readValueSource(r, objectAt(r, raw.reference, child(path, 'reference')), child(path, 'reference'), VALUE_SOURCE_KINDS) }),
        receivedAt: readValueSource(r, objectAt(r, raw.receivedAt, child(path, 'receivedAt')), child(path, 'receivedAt'), VALUE_SOURCE_KINDS),
        ...(raw.originalTotal === undefined
            ? {}
            : { originalTotal: readOriginalTotal(r, raw.originalTotal, child(path, 'originalTotal')) }),
        ...(raw.numberLocale === undefined
            ? {}
            : { numberLocale: oneOfAt<NumberLocale>(r, raw.numberLocale, numberLocalePath, ['dotDecimal', 'commaDecimal', 'indianComma']) }),
        ...(raw.dateLocale === undefined
            ? {}
            : { dateLocale: oneOfAt<DateLocale>(r, raw.dateLocale, dateLocalePath, ['dayFirst', 'monthFirst', 'yearFirst']) }),
    };
    return {
        ...mapping,
        ...(raw.templateHeadings === undefined
            ? {}
            : { templateHeadings: readTemplateHeadings(r, raw.templateHeadings, child(path, 'templateHeadings'), mapping) }),
        ...(raw.totalMustMatch === undefined ? {} : { totalMustMatch: booleanAt(r, raw.totalMustMatch, child(path, 'totalMustMatch')) }),
        ...(raw.requireOriginal === undefined ? {} : { requireOriginal: booleanAt(r, raw.requireOriginal, child(path, 'requireOriginal')) }),
    };
}

function readChannel(r: Reader, value: unknown, path: string, declared: Declared): LedgerChannel {
    const raw = objectAt(r, value, path);
    if (raw.kind !== 'manual' && raw.kind !== 'file') r.report(child(path, 'kind'), 'must be "manual" or "file"');
    const channelId = idAt(r, raw.channelId, child(path, 'channelId'));
    const label = textAt(r, raw.label, child(path, 'label'));
    if (raw.kind !== 'file') return { channelId, label, kind: 'manual' };
    const mappingsPath = child(path, 'mappings');
    const mappings = listAt(r, raw.mappings, mappingsPath, (entry, entryPath) => readStatementMapping(r, entry, entryPath, declared));
    if (Array.isArray(raw.mappings) && mappings.length === 0) r.report(mappingsPath, 'must hold at least one mapping');
    checkUnique(r, mappings.map((mapping) => mapping.mappingId), mappingsPath, 'mappingId');
    return { channelId, label, kind: 'file', mappings };
}

function readAdjustmentReason(r: Reader, value: unknown, path: string): LedgerAdjustmentReason {
    const raw = objectAt(r, value, path);
    const targetsPath = child(path, 'targets');
    const targets = listAt<LedgerAdjustmentTarget>(r, raw.targets, targetsPath, (entry, entryPath) =>
        oneOfAt(r, entry, entryPath, ['claim', 'payment']));
    if (Array.isArray(raw.targets) && targets.length === 0) r.report(targetsPath, 'must name at least one target');
    targets.forEach((target, index) => {
        if (Array.isArray(raw.targets) && raw.targets[index] === target && targets.indexOf(target) !== index) {
            r.report(item(targetsPath, index), `duplicates "${target}"`);
        }
    });
    return {
        reasonId: idAt(r, raw.reasonId, child(path, 'reasonId')),
        label: textAt(r, raw.label, child(path, 'label')),
        targets,
    };
}

function readMatchKey(r: Reader, value: unknown, path: string, declared: Declared): LedgerMatchKey {
    const raw = objectAt(r, value, path);
    if (raw.role === 'dimension') {
        return {
            role: 'dimension',
            dimensionId: declaredAt(r, raw.dimensionId, child(path, 'dimensionId'), declared.dimensionIds, 'dimension'),
        };
    }
    if (raw.role !== 'matchGroup') r.report(child(path, 'role'), 'must be "matchGroup" or "dimension"');
    return { role: 'matchGroup' };
}

function readTolerance(r: Reader, value: unknown, path: string): LedgerMatchTolerance {
    const raw = objectAt(r, value, path);
    if (raw.kind === 'percent') return { kind: 'percent', percent: numberAt(r, raw.percent, child(path, 'percent'), 0, 100) };
    if (raw.kind !== 'minorUnits') r.report(child(path, 'kind'), 'must be "minorUnits" or "percent"');
    return { kind: 'minorUnits', minorUnits: integerAt(r, raw.minorUnits, child(path, 'minorUnits'), 0) };
}

function readMatchRule(r: Reader, value: unknown, path: string, declared: Declared): LedgerMatchRule {
    const raw = objectAt(r, value, path);
    if ('autoCommitAbove' in raw) r.report(child(path, 'autoCommitAbove'), 'is refused; every allocation is committed by a person');
    const keysPath = child(path, 'keys');
    const keys = arrayAt(r, raw.keys, keysPath).map((key, index) => readMatchKey(r, key, item(keysPath, index), declared));
    if (Array.isArray(raw.keys) && keys.length === 0) r.report(keysPath, 'must hold at least one key');
    const counterpartyIdsPath = child(path, 'counterpartyIds');
    const counterpartyIds = raw.counterpartyIds === undefined
        ? undefined
        : arrayAt(r, raw.counterpartyIds, counterpartyIdsPath)
            .map((counterpartyId, index) => textAt(r, counterpartyId, item(counterpartyIdsPath, index)));
    return {
        matchRuleId: idAt(r, raw.matchRuleId, child(path, 'matchRuleId')),
        label: textAt(r, raw.label, child(path, 'label')),
        ...(counterpartyIds === undefined ? {} : { counterpartyIds }),
        keys,
        amountFrom: oneOfAt(r, raw.amountFrom, child(path, 'amountFrom'), ['remittanceLine', 'receiptTotal']),
        tolerance: readTolerance(r, raw.tolerance, child(path, 'tolerance')),
        score: numberAt(r, raw.score, child(path, 'score'), 0, 1),
    };
}

function readTermTrigger(r: Reader, value: unknown, path: string, declared: Declared): LedgerTermTrigger {
    const raw = objectAt(r, value, path);
    if (raw.role === 'dimension') {
        return {
            role: 'dimension',
            dimensionId: declaredAt(r, raw.dimensionId, child(path, 'dimensionId'), declared.dimensionIds, 'dimension'),
        };
    }
    if (raw.role !== 'triggerDate') r.report(child(path, 'role'), 'must be "triggerDate" or "dimension"');
    return { role: 'triggerDate' };
}

function readTermShape(r: Reader, value: unknown, path: string, triggers: ReadonlySet<string>): LedgerTermShape {
    const raw = objectAt(r, value, path);
    const triggerPath = child(path, 'trigger');
    const trigger = textAt(r, raw.trigger, triggerPath);
    if (trigger !== '' && !triggers.has(trigger)) r.report(triggerPath, 'must be a key of triggerRoles');
    if (raw.shape === 'monthLagDayOfMonth') {
        return {
            shape: 'monthLagDayOfMonth',
            trigger,
            monthsAfter: integerAt(r, raw.monthsAfter, child(path, 'monthsAfter'), 0),
            dayOfMonth: integerAt(r, raw.dayOfMonth, child(path, 'dayOfMonth'), 1, 31),
        };
    }
    if (raw.shape !== 'daysAfterTrigger') r.report(child(path, 'shape'), 'must be "daysAfterTrigger" or "monthLagDayOfMonth"');
    return { shape: 'daysAfterTrigger', trigger, days: integerAt(r, raw.days, child(path, 'days'), 0) };
}

function readTermParams(r: Reader, value: unknown, path: string): Partial<Record<LedgerTermParam, string>> {
    return Object.fromEntries(Object.entries(objectAt(r, value, path)).flatMap(([param, fieldId]): [LedgerTermParam, string][] => {
        const paramPath = child(path, param);
        const known = LEDGER_TERM_PARAMS.find((option) => option === param);
        if (known === undefined) {
            r.report(paramPath, `must be one of ${LEDGER_TERM_PARAMS.join(', ')}`);
            return [];
        }
        return [[known, textAt(r, fieldId, paramPath)]];
    }));
}

function readTerms(r: Reader, value: unknown, path: string, declared: Declared): LedgerTermsBinding {
    const raw = objectAt(r, value, path);
    const sourcePath = child(path, 'source');
    const source = objectAt(r, raw.source, sourcePath);
    const selectByPath = child(sourcePath, 'selectBy');
    const selectBy = source.selectBy === undefined
        ? undefined
        : arrayAt(r, source.selectBy, selectByPath).map((filter, index) => readRowFilter(r, filter, item(selectByPath, index)));
    const entityId = textAt(r, source.entityId, child(sourcePath, 'entityId'));
    const referenceKeyPath = child(sourcePath, 'counterpartyReferenceKey');
    const counterpartyReferenceKey = source.counterpartyReferenceKey === undefined
        ? undefined
        : textAt(r, source.counterpartyReferenceKey, referenceKeyPath);
    if (counterpartyReferenceKey === undefined && entityId !== '' && entityId !== declared.counterpartyEntityId) {
        r.report(referenceKeyPath, 'is required unless terms.source.entityId is the counterparty entity');
    }
    const shapeField = raw.shapeField === undefined ? undefined : textAt(r, raw.shapeField, child(path, 'shapeField'));
    const paramsPath = child(path, 'params');
    const params = raw.params === undefined ? undefined : readTermParams(r, raw.params, paramsPath);
    if (shapeField === undefined && params !== undefined && Object.keys(params).length > 0) {
        r.report(paramsPath, 'must be empty without a shapeField');
    }
    const priorityFieldPath = child(path, 'priorityField');
    const priorityField = raw.priorityField === undefined ? undefined : textAt(r, raw.priorityField, priorityFieldPath);
    if (shapeField === undefined && priorityField !== undefined) r.report(priorityFieldPath, 'requires a shapeField');
    const triggerRolesPath = child(path, 'triggerRoles');
    const triggerRoles = Object.fromEntries(Object.entries(objectAt(r, raw.triggerRoles, triggerRolesPath))
        .map(([trigger, role]) => [trigger, readTermTrigger(r, role, child(triggerRolesPath, trigger), declared)]));
    const triggers = new Set(Object.keys(triggerRoles));
    const fallback = raw.fallback === undefined ? undefined : readTermShape(r, raw.fallback, child(path, 'fallback'), triggers);
    if (shapeField === undefined && fallback === undefined) r.report(path, 'needs a shapeField or a fallback');
    return {
        source: {
            entityId,
            ...(counterpartyReferenceKey === undefined ? {} : { counterpartyReferenceKey }),
            ...(selectBy === undefined ? {} : { selectBy }),
        },
        ...(priorityField === undefined ? {} : { priorityField }),
        ...(shapeField === undefined ? {} : { shapeField }),
        ...(params === undefined ? {} : { params }),
        triggerRoles,
        ...(fallback === undefined ? {} : { fallback }),
    };
}

function readAging(r: Reader, value: unknown, path: string): LedgerAgingBuckets {
    const raw = objectAt(r, value, path);
    const bucketsPath = child(path, 'daysPastExpected');
    const buckets = arrayAt(r, raw.daysPastExpected, bucketsPath);
    return {
        daysPastExpected: buckets.map((days, index) => {
            const bucketPath = item(bucketsPath, index);
            const previous = buckets[index - 1];
            if (typeof days === 'number' && typeof previous === 'number' && days <= previous) {
                r.report(bucketPath, 'must be greater than the bucket before it');
                return days;
            }
            return integerAt(r, days, bucketPath, 1);
        }),
        ...(raw.unappliedAfterDays === undefined
            ? {}
            : { unappliedAfterDays: integerAt(r, raw.unappliedAfterDays, child(path, 'unappliedAfterDays'), 1) }),
    };
}

function readAccess(r: Reader, value: unknown, path: string): LedgerAccess {
    const raw = objectAt(r, value, path);
    const access = {
        readRight: rightAt(r, raw.readRight, child(path, 'readRight')),
        postRight: rightAt(r, raw.postRight, child(path, 'postRight')),
        commitRight: rightAt(r, raw.commitRight, child(path, 'commitRight')),
    };
    Object.entries(access)
        .filter(([, right]) => right === 'ledgerAdmin' || right === 'orgsAdmin')
        .forEach(([key]) => r.report(child(path, key), 'may not be an admin right'));
    return access;
}

function readCalendar(r: Reader, value: unknown, path: string): LedgerCalendarSettings {
    const raw = objectAt(r, value, path);
    const closePath = child(path, 'close');
    const close = objectAt(r, raw.close, closePath);
    return {
        startAt: momentAt(r, raw.startAt, child(path, 'startAt')),
        close: {
            cycle: oneOfAt(r, close.cycle, child(closePath, 'cycle'), ['none', 'period']),
            graceDays: integerAt(r, close.graceDays, child(closePath, 'graceDays'), 0),
        },
    };
}

function readDuration(r: Reader, value: unknown, path: string): LedgerDuration {
    const raw = objectAt(r, value, path);
    const units = LEDGER_DURATION_UNITS.flatMap((unit): [typeof unit, unknown][] => (raw[unit] === undefined ? [] : [[unit, raw[unit]]]));
    const whole = units.every(([, amount]) => typeof amount === 'number' && Number.isSafeInteger(amount) && amount >= 0);
    const duration: LedgerDuration = Object.fromEntries(units.map(([unit, amount]) => [unit, typeof amount === 'number' ? amount : 0]));
    if (isRecordValue(value) && (!whole || !Object.values(duration).some((amount) => amount > 0))) {
        r.report(path, 'must hold whole, non-negative years, months, weeks or days, at least one above zero');
    }
    return duration;
}

function readLifecycle(r: Reader, value: unknown, path: string): LedgerLifecyclePolicy {
    const raw = objectAt(r, value, path);
    const schedulePath = child(path, 'schedule');
    const schedule = objectAt(r, raw.schedule, schedulePath);
    const cronPath = child(schedulePath, 'cron');
    const cron = textAt(r, schedule.cron, cronPath);
    if (cron !== '' && parseLedgerCron(cron) === null) {
        r.report(cronPath, 'must be a five-field cron of minute, hour, day of month, month and day of week');
    }
    const duration = (key: string): LedgerDuration => readDuration(r, raw[key], child(path, key));
    return {
        archiveAfter: duration('archiveAfter'),
        retainFor: duration('retainFor'),
        proposalRetention: duration('proposalRetention'),
        sourceFileRetention: duration('sourceFileRetention'),
        outboxTtl: duration('outboxTtl'),
        schedule: { cron },
    };
}

function readCreditLineReasonId(r: Reader, value: unknown, reasons: readonly LedgerAdjustmentReason[]): string {
    const reasonId = idAt(r, value, 'creditLineReasonId');
    const reason = reasons.find((candidate) => candidate.reasonId === reasonId);
    if (reasonId !== '' && reason === undefined) r.report('creditLineReasonId', `"${reasonId}" is not a declared adjustment reason`);
    if (reason !== undefined && !reason.targets.includes('claim')) {
        r.report('creditLineReasonId', `adjustment reason "${reasonId}" must target a claim`);
    }
    return reasonId;
}

function listAt<T>(r: Reader, value: unknown, path: string, read: (entry: unknown, entryPath: string) => T): T[] {
    return arrayAt(r, value, path).map((entry, index) => read(entry, item(path, index)));
}

function validateLedgerDefinition(value: unknown): LedgerDefinitionCheck {
    if (!isRecordValue(value)) return { valid: false, problems: [{ path: '', message: 'must be an object' }] };
    const problems: LedgerDefinitionProblem[] = [];
    const r: Reader = { report: (path, message) => { problems.push({ path, message }); } };

    const dimensions = listAt(r, value.dimensions, 'dimensions', (entry, path) => readDimension(r, entry, path));
    const claimKinds = listAt(r, value.claimKinds, 'claimKinds', (entry, path) => readClaimKind(r, entry, path));
    if (Array.isArray(value.claimKinds) && claimKinds.length === 0) r.report('claimKinds', 'must hold at least one claim kind');
    const counterparty = objectAt(r, value.counterparty, 'counterparty');
    const counterpartyEntityId = textAt(r, counterparty.entityId, 'counterparty.entityId');
    const declared: Declared = {
        claimKindIds: new Set(claimKinds.map((kind) => kind.claimKindId)),
        dimensionIds: new Set(dimensions.map((dimension) => dimension.dimensionId)),
        counterpartyEntityId,
    };
    const accrualRules = listAt(r, value.accrualRules, 'accrualRules', (entry, path) => readAccrualRule(r, entry, path, declared));
    const channels = listAt(r, value.channels, 'channels', (entry, path) => readChannel(r, entry, path, declared));
    const matchRules = listAt(r, value.matchRules, 'matchRules', (entry, path) => readMatchRule(r, entry, path, declared));
    const adjustmentReasons = listAt(r, value.adjustmentReasons ?? [], 'adjustmentReasons', (entry, path) =>
        readAdjustmentReason(r, entry, path));

    checkUnique(r, dimensions.map((dimension) => dimension.dimensionId), 'dimensions', 'dimensionId');
    checkUnique(r, claimKinds.map((kind) => kind.claimKindId), 'claimKinds', 'claimKindId');
    checkUnique(r, accrualRules.map((rule) => rule.ruleId), 'accrualRules', 'ruleId');
    checkUnique(r, channels.map((channel) => channel.channelId), 'channels', 'channelId');
    checkUnique(r, matchRules.map((rule) => rule.matchRuleId), 'matchRules', 'matchRuleId');
    checkUnique(r, adjustmentReasons.map((reason) => reason.reasonId), 'adjustmentReasons', 'reasonId');
    checkAccrualOverlap(r, accrualRules);
    const creditLineReasonId = value.creditLineReasonId === undefined
        ? undefined
        : readCreditLineReasonId(r, value.creditLineReasonId, adjustmentReasons);

    if (value.description !== undefined && typeof value.description !== 'string') r.report('description', 'must be a string');
    if (value.matchGroupLabel !== undefined && typeof value.matchGroupLabel !== 'string') r.report('matchGroupLabel', 'must be a string');
    const matchGroupLabel = typeof value.matchGroupLabel === 'string' ? value.matchGroupLabel.trim() : '';
    const currency = typeof value.currency === 'string' && CURRENCY_PATTERN.test(value.currency) ? value.currency : '';
    if (currency === '') r.report('currency', 'must be an ISO-4217 code of three capital letters');

    const definition = {
        ledgerId: idAt(r, value.ledgerId, 'ledgerId'),
        label: textAt(r, value.label, 'label'),
        ...(typeof value.description === 'string' ? { description: value.description } : {}),
        rev: integerAt(r, value.rev, 'rev', 1),
        currency,
        calendar: readCalendar(r, value.calendar, 'calendar'),
        counterparty: { entityId: counterpartyEntityId },
        ...(matchGroupLabel === '' ? {} : { matchGroupLabel }),
        dimensions,
        claimKinds,
        accrualRules,
        channels,
        matchRules,
        ...(value.terms === undefined ? {} : { terms: readTerms(r, value.terms, 'terms', declared) }),
        aging: readAging(r, value.aging, 'aging'),
        adjustmentReasons,
        ...(creditLineReasonId === undefined ? {} : { creditLineReasonId }),
        access: readAccess(r, value.access, 'access'),
        ...(value.lifecycle === undefined ? {} : { lifecycle: readLifecycle(r, value.lifecycle, 'lifecycle') }),
    };
    return problems.length === 0 ? { valid: true, definition } : { valid: false, problems };
}

export { validateLedgerDefinition };
